<template>
  <div
    class="invoice-products-table-wrap"
    :class="{
      'invoice-products-table-wrap--guest': isGuestView && !printMode,
      'invoice-products-table-wrap--customer': isCustomerCompactView && !printMode,
    }"
  >
    <!-- Vista móvil: tarjetas agrupadas por proveedor -->
    <div
      v-if="isCardMode"
      class="invoice-supplier-cards"
    >
      <div
        v-for="group in supplierCardGroups"
        :key="group.key"
        class="invoice-supplier-card"
      >
        <div class="invoice-supplier-card__header">
          <b-badge
            class="text-capitalize invoice-supplier-card__name"
            variant="primary"
          >
            {{ group.supplierName }}
          </b-badge>
          <span
            v-if="quote.is_express && group.deliveryHours"
            class="invoice-supplier-card__delivery"
          >
            Entrega ~{{ formatHours(group.deliveryHours) }} hrs
          </span>
        </div>

        <div
          v-if="showSupplierFreight && group.items.length"
          class="supplier-freight supplier-freight--card"
        >
          <span class="supplier-freight__label">{{ $t('shipping') }}:</span>
          <span class="supplier-freight__amount">${{ supplierFreight(group.items[0]).amount | money }}</span>
          <b-badge
            v-if="supplierFreight(group.items[0]).overridden"
            variant="light-warning"
            class="supplier-freight__badge"
          >
            manual
          </b-badge>
          <b-button
            v-if="canEditFreight"
            size="sm"
            variant="flat-secondary"
            class="supplier-freight__action"
            @click="openFreightModal(group.items[0])"
          >
            <feather-icon
              icon="Edit2Icon"
              size="13"
            />
          </b-button>
        </div>

        <div
          v-for="item in group.items"
          :key="item.id"
          class="invoice-product-card"
        >
          <div class="invoice-product-card__top">
            <div class="invoice-product-card__title">
              <span class="invoice-product-card__product">{{ item.product_catalog.name }}</span>
              <span
                v-if="item.product_catalog && item.product_catalog.brand"
                class="invoice-product-card__brand"
              >
                {{ item.product_catalog.brand }}
              </span>
            </div>
            <span class="invoice-product-card__amount">${{ displayItemTotal(item) | money }}</span>
          </div>

          <div class="invoice-product-card__meta">
            <b-badge
              v-if="quote.is_express"
              pill
              :variant="item.is_express ? 'success' : 'secondary'"
            >
              {{ item.is_express ? $t('express') : $t('ordinary') }}
            </b-badge>
            <span class="invoice-product-card__qty">
              {{ item.quantity }} {{ $tc(unitsFilter(item.product_catalog.unit_type), item.quantity) }}
            </span>
            <span class="invoice-product-card__unit">
              × ${{ displayItemUnitPrice(item) | money }}
            </span>
          </div>

          <div
            v-if="getComparisonSavings(item).hasComparison"
            :class="[
              'invoice-product-card__savings',
              { 'invoice-product-card__savings--negative': getComparisonSavings(item).amount < 0 },
            ]"
          >
            {{ savingsLabel(getComparisonSavings(item)) }}
          </div>

          <!-- Desglose comparativo: mismo marcado y estilos que la vista web -->
          <b-button
            v-if="canShowComparison(item)"
            size="sm"
            variant="outline-primary"
            class="supplier-comparison-toggle"
            @click.stop="toggleComparison(item)"
          >
            <feather-icon
              :icon="isComparisonOpen(item) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="16"
              class="mr-25"
            />
            {{ isComparisonOpen(item) ? 'Ocultar detalle' : comparisonToggleLabel(item) }}
          </b-button>

          <div
            v-if="canShowComparison(item) && isComparisonOpen(item)"
            class="supplier-comparison-row no-print"
          >
            <div class="supplier-comparison-row__header">
              <div>
                <div class="supplier-comparison-row__title">
                  Por qué cuesta ${{ getCustomerUnitPrice(item) | money }}
                </div>
                <div class="supplier-comparison-row__subtitle">
                  {{ comparisonSubtitle(item) }}
                </div>
              </div>
              <div class="supplier-comparison-row__badge">
                {{ getSupplierLabel(item) || 'Proveedor seleccionado' }}
              </div>
            </div>

            <div
              v-if="getComparisonSavings(item).hasComparison"
              class="supplier-comparison-summary"
            >
              <div
                :class="[
                  'supplier-comparison-summary__amount',
                  { 'supplier-comparison-summary__amount--negative': getComparisonSavings(item).amount < 0 },
                ]"
              >
                {{ savingsLabel(getComparisonSavings(item)) }}
              </div>
              <div
                v-if="getComparisonSavings(item).averageUnitPrice > 0"
                class="supplier-comparison-summary__meta"
              >
                Promedio proveedores: {{ formatMoney(getComparisonSavings(item).averageUnitPrice) }}
                · Seleccionado: {{ formatMoney(getComparisonSavings(item).selectedUnitPrice) }}
                · Cantidad: {{ formatQuantity(getComparisonSavings(item).quantity) }}
              </div>
            </div>

            <div class="supplier-comparison-options">
              <div
                v-for="option in getComparisonOptions(item)"
                :key="option.key"
                :class="['supplier-comparison-option', {
                  'supplier-comparison-option--selected': option.isSelected,
                  'supplier-comparison-option--lowest': option.isLowest && !option.isSelected,
                }]"
              >
                <div class="supplier-comparison-option__supplier">
                  <span>{{ option.supplierName }}</span>
                  <membership-lock-chip
                    v-if="supplierNamesHidden"
                    :plan-name="paidPlanName"
                  />
                  <b-badge
                    v-if="option.isSelected"
                    variant="success"
                  >
                    seleccionado
                  </b-badge>
                  <b-badge
                    v-else-if="option.isLowest"
                    variant="light-success"
                  >
                    menor precio
                  </b-badge>
                </div>
                <div class="supplier-comparison-option__price">
                  {{ formatMoney(option.unitPrice) }}
                </div>
              </div>

              <div
                v-if="hiddenSuppliersCount"
                class="supplier-comparison-option supplier-comparison-option--locked"
              >
                <div class="supplier-comparison-option__supplier">
                  <span class="locked-dots">•••</span>
                  <span>
                    {{ hiddenSuppliersCount }}
                    {{ hiddenSuppliersCount === 1 ? 'proveedor más' : 'proveedores más' }}
                  </span>
                </div>
                <div class="supplier-comparison-option__price">
                  <b-link
                    class="locked-unlock"
                    :to="{ name: 'membership', query: { upgrade: 'comparative_suppliers' } }"
                  >
                    <feather-icon
                      icon="LockIcon"
                      size="14"
                      class="mr-25"
                    />
                    Desbloquear
                  </b-link>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="showWhatsappColumn"
            class="invoice-product-card__whatsapp"
          >
            <div
              v-if="canEditOrderNumber && isOrderNumberFormOpen(item)"
              class="d-flex align-items-center"
            >
              <input
                v-model="item.tempOrderNumber"
                type="text"
                class="form-control form-control-sm"
                placeholder="Número de pedido"
              >
              <b-button
                size="sm"
                variant="success"
                class="ml-1"
                :disabled="isSavingOrderNumber"
                @click="saveOrderNumber(item)"
              >
                Guardar
              </b-button>
              <b-button
                v-if="item.order_number"
                size="sm"
                variant="flat-secondary"
                class="ml-25"
                @click="cancelEditOrderNumber()"
              >
                {{ $t('cancel') }}
              </b-button>
            </div>
            <div
              v-else-if="item.order_number"
              class="d-flex align-items-center flex-wrap"
            >
              <a
                v-if="getPhone(item).phone"
                :href="whatsUrl(item)"
                target="_blank"
                class="invoice-product-card__whatsapp-link"
              >
                <feather-icon
                  icon="MessageCircleIcon"
                  size="16"
                  class="mr-25"
                />
                WhatsApp proveedor
              </a>
              <span
                v-else
                class="text-muted small"
              >
                {{ whatsappStatusText('no_phone') }}
              </span>
              <b-button
                v-if="canEditOrderNumber"
                size="sm"
                variant="flat-primary"
                class="ml-50 px-50"
                @click="startEditOrderNumber(item)"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                  class="mr-25"
                />
                OC #{{ item.order_number }}
              </b-button>
            </div>
            <span
              v-else
              class="text-muted small"
            >
              {{ whatsappStatusText('no_order') }}
            </span>
          </div>

          <div
            v-if="showActionsColumn"
            class="invoice-product-card__actions"
          >
            <b-button
              v-if="canOpenPriceModal"
              size="sm"
              variant="outline-primary"
              @click="openPriceModal(item)"
            >
              {{ $t('edit') }}
            </b-button>
            <b-button
              v-if="showDeleteColumn"
              size="sm"
              variant="outline-danger"
              @click="deleteItem(item)"
            >
              {{ $t('delete') }}
            </b-button>
          </div>
        </div>

        <div class="invoice-supplier-card__subtotal">
          <span>{{ $t('subtotal') }}</span>
          <strong>${{ group.subtotal | money }}</strong>
        </div>
      </div>
    </div>

    <b-table
      v-else
      :responsive="effectiveResponsive"
      :items="filteredProducts"
      :fields="computedFields"
      primary-key="id"
      :tbody-tr-class="rowClassForSupplierGroup"
    >
      <template #cell(is_express)="data">
        <h6 class="h-100 m-0">
          <b-badge
            v-if="quote.is_express"
            :id="`tooltip-express-${data.item.id}`"
            pill
            :variant="data.item.is_express ? 'success' : 'secondary'"
          >{{ data.item.is_express ? $t('express') : $t('ordinary') }}</b-badge>
        </h6>
      </template>
      <template v-slot:[`cell(product_catalog.name)`]="data">
        <div class="d-flex align-items-center">
          <div class="d-flex flex-column ml-1">
            <span>{{ data.value }}</span>
            <span
              class="text-muted"
              style="font-size: 12px;"
            >
              {{ (data.item.product_catalog && data.item.product_catalog.brand) || '' }}
            </span>
            <b-button
              v-if="canShowComparison(data.item)"
              size="sm"
              variant="outline-primary"
              class="supplier-comparison-toggle no-print"
              @click.stop="toggleComparison(data.item)"
            >
              <feather-icon
                :icon="isComparisonOpen(data.item) ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                size="16"
                class="mr-25"
              />
              {{ isComparisonOpen(data.item) ? 'Ocultar detalle' : comparisonToggleLabel(data.item) }}
            </b-button>
            <div
              v-if="getComparisonSavings(data.item).hasComparison"
              :class="[
                'supplier-comparison-savings',
                { 'supplier-comparison-savings--negative': getComparisonSavings(data.item).amount < 0 },
              ]"
            >
              {{ savingsLabel(getComparisonSavings(data.item)) }}
            </div>
          </div>

          <!-- Solo mostramos tooltip si existe el badge/target -->
          <span
            v-if="quote.is_express"
            class="d-flex"
          >
            <b-tooltip
              :target="`tooltip-express-${data.item.id}`"
              triggers="hover"
              boundary-padding="10"
            >
              <span>
                Entrega promedio en {{ formatHours(data.item.selected_supplier_branch_delivery_time_hours) }} Hrs.
              </span>
            </b-tooltip>
          </span>
        </div>
      </template>

      <template #cell(supplier)="data">
        <template v-if="shouldRenderSupplierGroupCell(data.item)">
          <div
            v-if="printMode"
            class="print-supplier"
          >
            {{ getSupplierLabel(data.item) || '—' }}
          </div>
          <h6
            v-else
            class="supplier-group-label m-0"
          >
            <b-badge
              class="text-capitalize"
              variant="primary"
            >
              {{ getSupplierLabel(data.item) }}
            </b-badge>
            <membership-lock-chip
              v-if="supplierNamesHidden"
              :plan-name="paidPlanName"
            />
          </h6>
          <div
            v-if="showSupplierFreight"
            class="supplier-freight"
          >
            <span class="supplier-freight__label">{{ $t('shipping') }}:</span>
            <span class="supplier-freight__amount">${{ supplierFreight(data.item).amount | money }}</span>
            <b-badge
              v-if="supplierFreight(data.item).overridden"
              variant="light-warning"
              class="supplier-freight__badge"
            >
              manual
            </b-badge>
            <b-button
              v-if="canEditFreight"
              size="sm"
              variant="flat-secondary"
              class="supplier-freight__action"
              @click="openFreightModal(data.item)"
            >
              <feather-icon
                icon="Edit2Icon"
                size="13"
              />
            </b-button>
          </div>
          <div
            v-if="!printMode"
            class="supplier-hours text-muted"
          >
            {{ formatHours(data.item.selected_supplier_branch_delivery_time_hours) }} hrs
          </div>
        </template>
      </template>

      <template #cell(unit_price)="data">
        <div class="d-flex align-items-center justify-content-end">
          <span>
            ${{ getCustomerUnitPrice(data.item) | money }}
          </span>
        </div>
      </template>
      <template #head(unit_price)>
        <span class="invoice-stacked-header">
          <span>Precio</span>
          <span>unitario</span>
        </span>
      </template>
      <template #head(unit_price_without_commission)>
        <span class="invoice-stacked-header">
          <span>Precio</span>
          <span>unitario</span>
        </span>
      </template>

      <template #cell(unit_price_without_commission)="data">
        <!-- S/IVA -->
        <span>${{ data.item.unit_price | money }}</span>
      </template>
      <template #cell(quantity)="data">
        <div class="d-flex flex-column align-items-center justify-content-center">
          <span>{{ data.item.quantity }}</span>
          <span
            v-if="!printMode"
            class="text-muted small"
          >
            {{ $tc(unitsFilter(data.item.product_catalog.unit_type), data.item.quantity) }}
          </span>
        </div>
      </template>

      <template #cell(item_deliveries)="data">
        <b-button
          variant="flat-secondary"
          class="delivery-progress"
          :class="deliveryProgressClass(data.item)"
          @click="openDeliveriesModal(data.item)"
        >
          <span class="delivery-progress__text">
            {{ formatDeliveredQuantity(deliveryRow(data.item).delivered_quantity) }} / {{ formatDeliveredQuantity(data.item.quantity) }}
          </span>
          <b-progress
            :value="deliveryPercent(data.item)"
            :max="100"
            height="5px"
            :variant="deliveryVariant(data.item)"
            class="delivery-progress__bar"
          />
        </b-button>
      </template>

      <template #cell(delivery_time_hours)="data">
        <template v-if="shouldRenderSupplierGroupCell(data.item)">
          <span class="text-muted small">
            {{ formatHours(data.item.selected_supplier_branch_delivery_time_hours) }}
          </span>
        </template>
      </template>

      <template #cell(unit_type)="data">
        <div>
          {{
            $tc(
              unitsFilter(data.item.product_catalog.unit_type),
              data.item.quantity,
            )
          }}
        </div>
      </template>
      <template #cell(total_without_commission)="data">
        <span>${{ data.value | money }}</span>
      </template>
      <template #cell(total)="data">
        <span>${{ data.value | money }}</span>
      </template>
      <template #cell(actions)="data">
        <div
          v-if="showActionsColumn"
          class="d-flex justify-content-center"
        >
          <b-dropdown
            no-caret
            right
            variant="flat-secondary"
            toggle-class="invoice-item-actions-toggle"
            menu-class="invoice-item-actions-menu"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
              />
            </template>

            <b-dropdown-item
              v-if="canOpenPriceModal"
              @click="openPriceModal(data.item)"
            >
              {{ $t('edit') }}
            </b-dropdown-item>

            <b-dropdown-item
              v-if="showDeleteColumn"
              class="text-danger"
              @click="deleteItem(data.item)"
            >
              {{ $t('delete') }}
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
      <template #cell(whatsapp)="data">
        <template v-if="shouldRenderSupplierGroupCell(data.item)">
        <div
          v-if="printMode"
          class="print-whatsapp"
        >
          <a
            v-if="data.item.order_number && getPhone(data.item).phone"
            :href="whatsUrl(data.item)"
            target="_blank"
            class="print-whatsapp-link"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              width="28"
              height="28"
              fill="#25D366"
            >
              <path d="M380.9 97.1C339.3 55.5 285.4 32 224.1 32 109.5 32 16 125.5 16 240c0 45.6 12.2 90.1 35.3 129.1L0 480l112.2-48.1c37.7 20.5 80.2 31.5 123.9 31.5 114.6 0 208.1-93.5 208.1-208.1 0-61.2-23.9-115.2-67.3-158.3zM224.1 438.5c-38.5 0-76-10.5-108.6-30.4l-7.8-4.7-66.9 28 28.6-65.4-5.1-8.3c-21.3-35.2-32.5-75.7-32.5-117.6 0-123 100-223 223-223 59.9 0 116.2 23.3 158.5 65.6s65.6 98.5 65.6 158.5c0 123-100 223-223 223zm124.3-146.1l-27.8-13c-15.2-7.1-26.3-13-37.6 2.2-10.8 14.6-20.2 26.7-34.5 21.1-38.4-15.4-77.6-43.1-102.4-76.6-11-14.8 11-13.7 25.3-45.8 2.5-5.4 1.2-10.1-0.6-14.1l-12-28.2c-2.9-6.7-5.9-12.7-14.6-10.8-10.6 2.3-33.1 19.9-39.1 39.1-7.8 25.5-1.7 58.3 35.9 95.3 41.7 41.2 81.7 64.7 121.4 78.1 12.6 4.4 24 3.8 33-3.7 10.4-8.6 23.2-27.2 35.7-43.6 5.6-7.3 11.5-10.3 18.6-10.3 6.3 0 13.9-0.3 21.3 3.3 8.5 4.3 19.9 9.9 30.4 14.9 8.6 4.1 16 4.9 21.7-4.2 5.1-8.2 5.1-20.1 3.6-23.7-1.5-3.6-8.2-5.9-16.5-10.2z" />
            </svg>
            <span class="print-whatsapp-hint">Dar Click</span>
          </a>
          <span
            v-else-if="data.item.order_number && !getPhone(data.item).phone"
            class="text-muted whatsapp-status"
          >
            {{ whatsappStatusText('no_phone') }}
          </span>
          <span
            v-else
            class="text-muted whatsapp-status"
          >
            {{ whatsappStatusText('no_order') }}
          </span>
        </div>
        <div
          v-else
          class="d-flex justify-content-center align-items-center"
        >

          <!-- Administrador: captura o edición del número de pedido -->
          <div
            v-if="canEditOrderNumber && isOrderNumberFormOpen(data.item)"
            class="order-number-form"
          >
            <input
              v-model="data.item.tempOrderNumber"
              type="text"
              class="form-control form-control-sm"
              placeholder="Número de pedido"
            >
            <div class="order-number-form__actions">
              <b-button
                size="sm"
                variant="success"
                :disabled="isSavingOrderNumber"
                @click="saveOrderNumber(data.item)"
              >
                Guardar
              </b-button>
              <b-button
                v-if="data.item.order_number"
                size="sm"
                variant="flat-secondary"
                @click="cancelEditOrderNumber()"
              >
                {{ $t('cancel') }}
              </b-button>
            </div>
          </div>

          <!-- Cliente: Mensaje si no hay número de pedido -->
          <div v-else-if="!data.item.order_number">
            <span class="text-muted whatsapp-status">{{ whatsappStatusText('no_order') }}</span>
          </div>

          <!-- Con OC: WhatsApp (si hay teléfono) y edición para el administrador -->
          <div
            v-else
            class="d-flex flex-column align-items-center"
          >
            <a
              v-if="getPhone(data.item).phone"
              :href="whatsUrl(data.item)"
              target="_blank"
              class="whatsapp-icon-link"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                width="24"
                height="24"
                fill="#25D366"
              >
                <path d="M380.9 97.1C339.3 55.5 285.4 32 224.1 32 109.5 32 16 125.5 16 240c0 45.6 12.2 90.1 35.3 129.1L0 480l112.2-48.1c37.7 20.5 80.2 31.5 123.9 31.5 114.6 0 208.1-93.5 208.1-208.1 0-61.2-23.9-115.2-67.3-158.3zM224.1 438.5c-38.5 0-76-10.5-108.6-30.4l-7.8-4.7-66.9 28 28.6-65.4-5.1-8.3c-21.3-35.2-32.5-75.7-32.5-117.6 0-123 100-223 223-223 59.9 0 116.2 23.3 158.5 65.6s65.6 98.5 65.6 158.5c0 123-100 223-223 223zm124.3-146.1l-27.8-13c-15.2-7.1-26.3-13-37.6 2.2-10.8 14.6-20.2 26.7-34.5 21.1-38.4-15.4-77.6-43.1-102.4-76.6-11-14.8 11-13.7 25.3-45.8 2.5-5.4 1.2-10.1-0.6-14.1l-12-28.2c-2.9-6.7-5.9-12.7-14.6-10.8-10.6 2.3-33.1 19.9-39.1 39.1-7.8 25.5-1.7 58.3 35.9 95.3 41.7 41.2 81.7 64.7 121.4 78.1 12.6 4.4 24 3.8 33-3.7 10.4-8.6 23.2-27.2 35.7-43.6 5.6-7.3 11.5-10.3 18.6-10.3 6.3 0 13.9-0.3 21.3 3.3 8.5 4.3 19.9 9.9 30.4 14.9 8.6 4.1 16 4.9 21.7-4.2 5.1-8.2 5.1-20.1 3.6-23.7-1.5-3.6-8.2-5.9-16.5-10.2z" />
              </svg>
            </a>
            <span
              v-else
              class="text-muted whatsapp-status"
            >
              {{ whatsappStatusText('no_phone') }}
            </span>

            <b-button
              v-if="canEditOrderNumber"
              size="sm"
              variant="flat-primary"
              class="order-number-edit no-print"
              @click="startEditOrderNumber(data.item)"
            >
              <feather-icon
                icon="EditIcon"
                size="12"
                class="mr-25"
              />
              #{{ data.item.order_number }}
            </b-button>
          </div>

        </div>
        </template>
      </template>

      <template #row-details="data">
        <div
          v-if="canShowComparison(data.item)"
          class="supplier-comparison-row no-print"
        >
          <div class="supplier-comparison-row__header">
            <div>
              <div class="supplier-comparison-row__title">
                Por qué cuesta ${{ getCustomerUnitPrice(data.item) | money }}
              </div>
              <div class="supplier-comparison-row__subtitle">
                {{ comparisonSubtitle(data.item) }}
              </div>
            </div>
            <div class="supplier-comparison-row__badge">
              {{ getSupplierLabel(data.item) || 'Proveedor seleccionado' }}
            </div>
          </div>

          <div
            v-if="getComparisonSavings(data.item).hasComparison"
            class="supplier-comparison-summary"
          >
            <div
              :class="[
                'supplier-comparison-summary__amount',
                { 'supplier-comparison-summary__amount--negative': getComparisonSavings(data.item).amount < 0 },
              ]"
            >
              {{ savingsLabel(getComparisonSavings(data.item)) }}
            </div>
            <div class="supplier-comparison-summary__meta">
              Promedio proveedores: {{ formatMoney(getComparisonSavings(data.item).averageUnitPrice) }}
              · Seleccionado: {{ formatMoney(getComparisonSavings(data.item).selectedUnitPrice) }}
              · Cantidad: {{ formatQuantity(getComparisonSavings(data.item).quantity) }}
            </div>
          </div>

          <div class="supplier-comparison-options">
            <div
              v-for="option in getComparisonOptions(data.item)"
              :key="option.key"
              :class="['supplier-comparison-option', {
                'supplier-comparison-option--selected': option.isSelected,
                'supplier-comparison-option--lowest': option.isLowest && !option.isSelected,
              }]"
            >
              <div class="supplier-comparison-option__supplier">
                <span>{{ option.supplierName }}</span>
                <membership-lock-chip
                  v-if="supplierNamesHidden"
                  :plan-name="paidPlanName"
                />
                <b-badge
                  v-if="option.isSelected"
                  variant="success"
                >
                  seleccionado
                </b-badge>
                <b-badge
                  v-else-if="option.isLowest"
                  variant="light-success"
                >
                  menor precio
                </b-badge>
              </div>
              <div class="supplier-comparison-option__price">
                {{ formatMoney(option.unitPrice) }}
              </div>
            </div>

            <div
              v-if="hiddenSuppliersCount"
              class="supplier-comparison-option supplier-comparison-option--locked"
            >
              <div class="supplier-comparison-option__supplier">
                <span class="locked-dots">•••</span>
                <span>
                  {{ hiddenSuppliersCount }}
                  {{ hiddenSuppliersCount === 1 ? 'proveedor más' : 'proveedores más' }}
                </span>
              </div>
              <div class="supplier-comparison-option__price">
                <b-link
                  class="locked-unlock"
                  :to="{ name: 'membership', query: { upgrade: 'comparative_suppliers' } }"
                >
                  <feather-icon
                    icon="LockIcon"
                    size="14"
                    class="mr-25"
                  />
                  Desbloquear
                </b-link>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-table>
    <b-modal
      id="item-deliveries-modal"
      ref="itemDeliveriesModal"
      :title="$t('itemDeliveries.title')"
      hide-footer
      size="lg"
    >
      <div v-if="deliveriesItem">
        <div class="d-flex justify-content-between align-items-start mb-1">
          <div>
            <strong>{{ deliveriesItem.product_catalog && deliveriesItem.product_catalog.name }}</strong>
            <div class="text-muted small">
              {{ getSupplierLabel(deliveriesItem) }}
            </div>
          </div>
          <b-badge :variant="deliveryVariant(deliveriesItem)">
            {{ formatDeliveredQuantity(deliveryRow(deliveriesItem).delivered_quantity) }} / {{ formatDeliveredQuantity(deliveriesItem.quantity) }}
            {{ $tc(unitsFilter(deliveriesItem.product_catalog.unit_type), deliveriesItem.quantity) }}
          </b-badge>
        </div>
        <b-progress
          :value="deliveryPercent(deliveriesItem)"
          :max="100"
          height="8px"
          :variant="deliveryVariant(deliveriesItem)"
          class="mb-50"
        />
        <small class="text-muted d-block mb-2">
          <template v-if="deliveryRow(deliveriesItem).fully_delivered">
            {{ $t('itemDeliveries.complete') }}
          </template>
          <template v-else>
            {{ $t('itemDeliveries.pending', {
              quantity: formatDeliveredQuantity(deliveryRow(deliveriesItem).pending_quantity),
              unit: $tc(unitsFilter(deliveriesItem.product_catalog.unit_type), deliveryRow(deliveriesItem).pending_quantity),
            }) }}
          </template>
        </small>

        <b-row v-if="!deliveryRow(deliveriesItem).fully_delivered">
          <b-col md="4">
            <b-form-group :label="$t('itemDeliveries.quantity')">
              <b-form-input
                v-model.number="deliveryForm.quantity"
                type="number"
                step="0.01"
                min="0.01"
                :max="deliveryRow(deliveriesItem).pending_quantity"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :label="$t('itemDeliveries.date')">
              <b-form-datepicker
                v-model="deliveryForm.delivered_at"
                :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                :max="todayIsoDate"
                locale="es"
                hide-header
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group :label="$t('itemDeliveries.note')">
              <b-form-input
                v-model="deliveryForm.note"
                :placeholder="$t('itemDeliveries.notePlaceholder')"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            class="text-right mb-2"
          >
            <b-button
              variant="primary"
              :disabled="isSavingDelivery || !(deliveryForm.quantity > 0)"
              @click="submitDelivery"
            >
              <b-spinner
                v-if="isSavingDelivery"
                small
                class="mr-50"
              />
              {{ $t('itemDeliveries.register') }}
            </b-button>
          </b-col>
        </b-row>

        <h6 class="mb-50">
          {{ $t('itemDeliveries.history') }}
        </h6>
        <b-list-group
          v-if="deliveryRow(deliveriesItem).deliveries.length"
          flush
        >
          <b-list-group-item
            v-for="delivery in deliveryRow(deliveriesItem).deliveries"
            :key="delivery.id"
            class="d-flex align-items-center px-0"
          >
            <div class="flex-grow-1">
              <strong>{{ formatDeliveredQuantity(delivery.quantity) }}</strong>
              {{ $tc(unitsFilter(deliveriesItem.product_catalog.unit_type), delivery.quantity) }}
              <span class="text-muted">· {{ delivery.delivered_at | date }}</span>
              <small
                v-if="delivery.registered_by_name"
                class="text-muted"
              >
                {{ $t('itemDeliveries.by', { name: delivery.registered_by_name }) }}
              </small>
              <div
                v-if="delivery.note"
                class="small text-muted"
              >
                {{ delivery.note }}
              </div>
            </div>
            <b-button
              size="sm"
              variant="flat-danger"
              class="btn-icon"
              :disabled="deletingDeliveryId === delivery.id"
              @click="removeDelivery(delivery)"
            >
              <b-spinner
                v-if="deletingDeliveryId === delivery.id"
                small
              />
              <feather-icon
                v-else
                icon="Trash2Icon"
              />
            </b-button>
          </b-list-group-item>
        </b-list-group>
        <p
          v-else
          class="text-muted small mb-0"
        >
          {{ $t('itemDeliveries.none') }}
        </p>
      </div>
    </b-modal>

    <b-modal
      id="edit-prices-modal"
      ref="editPricesModal"
      :title="$t('invoiceEditItemTitle')"
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="isPriceEditModalBusy"
      @ok="savePrices"
    >
      <div v-if="editingItem">
        <p class="mb-1">
          <strong>{{ editingItem.product_catalog && editingItem.product_catalog.name }}</strong>
        </p>

        <small
          v-if="isOnlySupplierPriceEditable"
          class="text-muted d-block mb-1"
        >
          {{ $t('invoiceEditItemAccountingLockNote') }}
        </small>

        <b-form-group :label="$t('supplier')">
          <b-form-select
            v-model="editForm.supplier_branch_id"
            :options="editSupplierOptions"
            :disabled="isLoadingEditSuppliers || !editSupplierOptions.length || !!editMergeTarget"
            @change="handleEditSupplierChange"
          />
          <small
            v-if="isLoadingEditSuppliers || !editSupplierOptions.length"
            class="text-muted d-block mt-50"
          >
            {{ isLoadingEditSuppliers ? $t('invoiceEditItemSupplierLoading') : $t('invoiceEditItemSupplierUnavailable') }}
          </small>
        </b-form-group>

        <b-form-group
          v-if="!isOnlySupplierPriceEditable"
          :label="$t('product')"
        >
          <v-select
            v-model="editForm.product_catalog_id"
            :options="editProductOptions"
            :reduce="option => option.value"
            :filterable="false"
            :clearable="false"
            :loading="isLoadingEditProducts"
            :disabled="!editForm.supplier_branch_id"
            :placeholder="$t('invoiceEditItemProductSearch')"
            @search="searchEditProducts"
            @input="handleEditProductChange"
          >
            <template #option="option">
              <div class="d-flex flex-column">
                <span>{{ option.label }}</span>
                <small class="text-muted">{{ option.meta }}</small>
              </div>
            </template>
            <template #no-options>
              {{ $t('invoiceEditItemProductNoResults') }}
            </template>
          </v-select>
          <small class="text-muted d-block mt-50">
            {{ $t('invoiceEditItemProductHint') }}
          </small>
        </b-form-group>

        <b-alert
          v-if="editMergeTarget"
          variant="warning"
          show
          class="p-1 mb-1"
        >
          <small>
            {{ $t('invoiceEditItemMergeNotice', {
              quantity: editMergeTarget.quantity,
              unit: $tc(unitsFilter(editMergeTarget.product_catalog.unit_type), editMergeTarget.quantity),
              supplier: getSupplierLabel(editMergeTarget),
            }) }}
          </small>
        </b-alert>

        <b-form-group :label="$t('invoiceEditItemBasePrice')">
          <currency-input
            v-model="editForm.unit_price"
            :precision="4"
            :min="0"
            :disabled="!!editMergeTarget"
          />
        </b-form-group>

        <b-form-group :label="$t('invoiceEditItemCustomerUnitPrice')">
          <currency-input
            v-model="editForm.customer_unit_price"
            :precision="4"
            :min="0"
            :disabled="isOnlySupplierPriceEditable || !!editMergeTarget"
          />
        </b-form-group>

        <b-form-group :label="$t('quantity')">
          <b-form-input
            v-model.number="editForm.quantity"
            type="number"
            step="0.01"
            min="0.01"
            :disabled="isOnlySupplierPriceEditable"
          />
        </b-form-group>

        <div class="mt-2">
          <small class="text-muted">
            {{ $t('invoiceEditItemFinalUnitPrice') }}:
            <strong>
              ${{ Number(editForm.customer_unit_price || 0) | money }}
            </strong>
          </small>
        </div>

        <div class="mt-1">
          <small class="text-muted">
            {{ $t('invoiceEditItemUnitResult') }}:
            <strong :class="unitResultClass(editForm.unit_price, editForm.customer_unit_price)">
              {{ unitResultLabel(editForm.unit_price, editForm.customer_unit_price) }}
            </strong>
          </small>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="edit-freight-modal"
      ref="editFreightModal"
      title="Editar flete"
      ok-title="Guardar"
      cancel-title="Cancelar"
      :ok-disabled="isSavingFreight"
      @ok="saveFreight"
    >
      <div v-if="editingFreight">
        <p class="mb-1">
          <strong>{{ editingFreight.supplierName }}</strong>
        </p>

        <b-form-group label="Flete del proveedor">
          <currency-input
            v-model="freightForm.amount"
            :precision="2"
            :min="0"
          />
        </b-form-group>

        <b-alert
          v-if="freightWarning"
          variant="warning"
          show
          class="p-1 mb-1"
        >
          <small>{{ freightWarning }}</small>
        </b-alert>

        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            Flete actual: <strong>${{ editingFreight.amount | money }}</strong>
          </small>
          <b-button
            v-if="editingFreight.overridden"
            size="sm"
            variant="flat-secondary"
            :disabled="isSavingFreight"
            @click="restoreAutomaticFreight"
          >
            Volver al automático
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      id="delete-quote-item-modal"
      ref="deleteQuoteItemModal"
      title="Confirmar eliminación"
      ok-title="Eliminar"
      ok-variant="danger"
      cancel-title="Cancelar"
      @ok="confirmDeleteItem"
      @hidden="pendingDeleteItem = null"
    >
      <div v-if="pendingDeleteItem">
        ¿Seguro que deseas eliminar la partida
        <strong>{{ pendingDeleteItem.product_catalog && pendingDeleteItem.product_catalog.name }}</strong>?
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import vSelect from 'vue-select'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'
import MembershipLockChip from '@/components/invoice/MembershipLockChip.vue'
import { todayLocalDate } from '@/utils/expenseDates'

export default {
  components: { CurrencyInput, MembershipLockChip, vSelect },
  props: {
    products: {
      type: Array,
      default() {
        return []
      },
    },
    supplierData: {
      type: Array,
      default() {
        return []
      },
    },
    projectData: {
      type: Array,
      default() {
        return []
      },
    },
    quoteData: {
      type: Array,
      default() {
        return []
      },
    },
    // Cuando la cotizacion muestra la comparativa en tabla, el desglose por partida sobra:
    // seria la misma informacion dos veces en la misma pantalla.
    hideComparison: {
      type: Boolean,
      default: false,
    },
    printMode: { type: Boolean, default: false },
    responsive: { type: [Boolean, String], default: true },
    cardMode: { type: Boolean, default: false },
  },

  data() {
    return {
      fields: [
        {
          key: 'is_express',
          label: this.$t('delivery'),
        },
        {
          key: 'product_catalog.name',
          label: this.$t('products'),
          tdClass: 'invoice-product-col',
          thClass: 'invoice-product-col',
        },
        {
          key: 'supplier',
          label: this.$t('supplier'),
          tdClass: 'invoice-supplier-col',
          thClass: 'invoice-supplier-col',
        },
        {
          key: 'whatsapp',
          label: this.$t('whatsapp'),
          tdClass: 'text-center invoice-compact-col',
          thClass: 'text-center invoice-compact-col',
        },
        {
          key: 'quantity',
          label: this.$t('quantity'),
          tdClass: 'text-center invoice-compact-col',
          thClass: 'text-center invoice-compact-col',
        },
        {
          key: 'unit_price',
          label: this.$t('unitPrice'),
          tdClass: 'text-right text-nowrap invoice-money-col',
          thClass: 'text-right invoice-money-col',
          thStyle: { width: '1%' },
        },
        {
          key: 'total',
          label: this.$t('total'),
          tdClass: 'text-right text-nowrap invoice-money-col',
          thClass: 'text-right invoice-money-col',
          thStyle: { width: '1%' },
        },
      ],
      editingItem: null,
      editForm: {
        supplier_branch_id: null,
        product_catalog_id: null,
        unit_price: 0,
        customer_unit_price: 0,
        quantity: 1,
      },
      editSupplierOptions: [],
      editProductOptions: [],
      isLoadingEditProducts: false,
      editProductSearchTimer: null,
      editProductSearchSeq: 0,
      deliveriesItem: null,
      deliveryForm: { quantity: null, delivered_at: todayLocalDate(), note: '' },
      isSavingDelivery: false,
      deletingDeliveryId: null,
      allSuppliersById: {},
      allSuppliersLoaded: false,
      allSuppliersLoadPromise: null,
      isLoadingEditSuppliers: false,
      isSavingPrices: false,
      pendingDeleteItem: null,

      // <<< SNAPSHOT DE PROVEEDORES >>>
      supplierLabelsByItemId: {}, // { [quote_item_id]: 'Nombre proveedor' }
      suppliersInitialized: false,
      expandedComparisonItemIds: {},
      editingOrderNumberId: null,
      isSavingOrderNumber: false,
      editingFreight: null,
      freightForm: { amount: 0 },
      isSavingFreight: false,
    }
  },

  computed: {
    ...mapGetters('app', ['unitsFilter', 'isSmallScreen']),
    ...mapGetters('quotes', ['quote', 'itemDeliveriesByQuoteItem', 'itemDeliveriesQuoteId']),
    ...mapGetters('auth', ['currentUser']),

    // El nombre del proveedor va enmascarado cuando el plan del cliente no lo incluye. Al
    // equipo el backend no le manda estas pistas, asi que a ellos no les sale el candado.
    supplierNamesHidden() {
      const hints = this.quote?.membership_hints

      return Boolean(hints?.plan_key === 'free' && hints?.supplier_names_hidden)
    },

    paidPlanName() {
      return this.quote?.membership_hints?.paid_plan_name || 'de paga'
    },

    hiddenSuppliersCount() {
      const hints = this.quote?.membership_hints
      if (hints?.plan_key !== 'free') return 0

      return Number(hints?.comparative_suppliers_hidden || 0)
    },
    isGuestView() {
      return !this.currentUser?.role_name
    },
    isCustomerCompactView() {
      return this.currentUser?.role_name === 'customer'
    },
    effectiveResponsive() {
      return false
    },
    // Por debajo del breakpoint lg la tabla no cabe: ahí se muestran las tarjetas
    // agrupadas por proveedor. Impresión siempre usa tabla.
    isCardMode() {
      return (this.cardMode || this.isSmallScreen) && !this.printMode
    },
    // En la orden de compra del proveedor se muestran precios netos (sin comisión), igual que en la tabla.
    showSupplierNetPrices() {
      return this.$route.name === 'purchaseOrder'
        && ['auctioneer', 'supplier_admin'].includes(this.currentUser.role_name)
    },
    // Agrupa las partidas por proveedor para la vista de tarjetas móvil, con subtotal por grupo.
    supplierCardGroups() {
      const groups = []
      const byKey = new Map()

      this.filteredProducts.forEach(item => {
        const key = this.supplierGroupKey(item)
        let group = byKey.get(key)

        if (!group) {
          group = {
            key,
            supplierName: this.getSupplierLabel(item) || this.$t('supplier'),
            deliveryHours: item.selected_supplier_branch_delivery_time_hours,
            items: [],
            subtotal: 0,
          }
          byKey.set(key, group)
          groups.push(group)
        }

        group.items.push(item)
        group.subtotal += Number(this.displayItemTotal(item)) || 0
      })

      return groups
    },
    mergeSupplierCells() {
      return this.printMode || !Object.values(this.expandedComparisonItemIds).some(Boolean)
    },

    filteredProducts() {
      const filtered = this.products.filter(
        product => product.selected_supplier_branch_id && product.active_status,
      )

      const isAdminEditing = this.currentUser.role_name === 'admin' && !this.printMode
      const baseRows = isAdminEditing ? filtered : (() => {
        const groupedMap = new Map()

        filtered.forEach(item => {
          const key = (item.product_catalog && item.product_catalog.id) || item.product_catalog_id

          if (groupedMap.has(key)) {
            const existing = groupedMap.get(key)
            existing.quantity += Number(item.quantity)
            existing.total += Number(item.total)
            existing.total_without_commission += Number(item.total_without_commission || 0)
          } else {
            groupedMap.set(key, {
              ...item,
              quantity: Number(item.quantity),
              total: Number(item.total),
              total_without_commission: Number(item.total_without_commission || 0),
            })
          }
        })
        return Array.from(groupedMap.values())
      })()

      const sortedRows = [...baseRows]
        .map(row => ({
          ...row,
          // Tambien se consulta canShowComparison: si el detalle esta apagado, el contenido
          // de la fila no se pinta y b-table dejaria un renglon vacio.
          _showDetails: this.canShowComparison(row) && Boolean(this.expandedComparisonItemIds[row.id]),
        }))
        .sort((a, b) => this.compareRowsForDisplay(a, b))

      return this.attachSupplierRowspans(sortedRows)
    },

    showWhatsappColumn() {
      const isPurchaseOrderView = this.$route.name === 'purchaseOrder'
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})
      const paymentStatus = String(q?.payment_status || '')
      const quoteStatus = String(q?.quote_status || '')
      const hasApprovedPayment = ['payment_approved', 'credit_approved'].includes(paymentStatus)
      const hasPurchasedStatus = ['bid_approved', 'buyout'].includes(quoteStatus)
      const hasAccountingLock = Boolean(
        q?.is_paid
        || q?.is_credit
        || hasApprovedPayment
        || Number(q?.paid_amount || 0) > 0,
      )

      return isPurchaseOrderView || (hasPurchasedStatus && hasAccountingLock)
    },
    activeQuote() {
      return (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})
    },
    // El flete se reparte entre los proveedores de la cotización, así que el desglose
    // solo se le muestra a admin: al cliente le llega ya sumado en los totales.
    showSupplierFreight() {
      return this.currentUser.role_name === 'admin' && !this.printMode
    },
    canEditFreight() {
      return this.showSupplierFreight
        && this.activeQuote?.quote_status !== 'deleted'
        && !this.isQuoteItemsLocked
    },
    freightWarning() {
      if (!this.editingFreight) return ''

      const q = this.activeQuote
      const settled = Boolean(q?.is_paid)
        || Boolean(q?.is_credit)
        || ['payment_approved', 'credit_approved'].includes(q?.payment_status)
      const diff = Number(this.freightForm.amount || 0) - Number(this.editingFreight.amount || 0)

      if (!settled || Math.abs(diff) < 0.01) {
        return Number(q?.total_purchase_orders || 0) > 0
          ? 'Esta cotización ya tiene órdenes de compra: cambiar el flete mueve lo que se le debe al proveedor.'
          : ''
      }

      return diff > 0
        ? `Esta cotización ya está pagada: subir el flete deja $${diff.toFixed(2)} pendientes de cobro.`
        : `Esta cotización ya está pagada: bajar el flete deja $${Math.abs(diff).toFixed(2)} a favor del cliente.`
    },
    isQuoteItemsLocked() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return Boolean(q?.items_locked_for_accounting)
    },
    canEditItemPrices() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return this.currentUser.role_name === 'admin'
        && !this.printMode
        && q?.quote_status !== 'deleted'
        && !this.isQuoteItemsLocked
    },
    canOpenPriceModal() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return this.currentUser.role_name === 'admin'
        && !this.printMode
        && q?.quote_status !== 'deleted'
    },
    isOnlySupplierPriceEditable() {
      return this.isQuoteItemsLocked
    },
    isPriceEditModalBusy() {
      return this.isSavingPrices || (!this.isOnlySupplierPriceEditable && this.isLoadingEditSuppliers)
    },
    showDeliveriesColumn() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return this.currentUser.role_name === 'admin'
        && !this.printMode
        && this.$route.name === 'purchaseOrder'
        && ['payment_approved', 'credit_approved'].includes(q?.payment_status)
    },
    todayIsoDate() {
      return todayLocalDate()
    },
    editMergeTarget() {
      const productId = String(this.editForm.product_catalog_id || '')
      if (!this.editingItem || !productId || productId === String(this.editingItem.product_catalog_id || '')) return null

      return this.products.find(
        item => item.id !== this.editingItem.id
          && (item.active_status === 'active' || item.active_status === true)
          && String(item.product_catalog_id || '') === productId,
      ) || null
    },
    currentQuoteId() {
      return this.quoteData?.id || this.quote?.id || this.$route.params.quoteId
    },
    showDeleteColumn() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return this.currentUser.role_name === 'admin'
        && !this.printMode
        && ['checkout', 'purchaseOrder'].includes(this.$route.name)
        && q?.quote_status !== 'deleted'
        && !this.isQuoteItemsLocked
    },
    showActionsColumn() {
      return this.canOpenPriceModal || this.showDeleteColumn
    },
    // El número de pedido es una referencia logística del proveedor: no toca
    // importes ni el CFDI, así que sigue siendo editable con la cotización
    // ya facturada, a diferencia de precios y partidas.
    canEditOrderNumber() {
      const q = (this.quoteData && Object.keys(this.quoteData).length > 0)
        ? this.quoteData
        : (this.quote || {})

      return this.currentUser.role_name === 'admin'
        && !this.printMode
        && q?.quote_status !== 'deleted'
    },

    computedFields() {
      const isPOForSupplier = this.$route.name === 'purchaseOrder'
        && ['auctioneer', 'supplier_admin'].includes(this.currentUser.role_name)

      // ====== MODO IMPRESIÓN ======
      if (this.printMode) {
        const printFields = [
          { key: 'product_catalog.name', label: this.$t('products') },
          { key: 'supplier', label: this.$t('supplier') },

          // ✅ NUEVO: Hrs
          {
            key: 'delivery_time_hours',
            label: 'Hrs',
            thClass: 'text-center',
            tdClass: 'text-center',
            thStyle: { width: '60px' },
          },

          {
            key: 'quantity',
            label: this.$t('quantity'),
            tdClass: 'text-center',
            thClass: 'text-center',
          },
          {
            key: 'unit_type',
            label: this.$t('unit'),
            tdClass: 'text-center',
            thClass: 'text-center',
          },
          isPOForSupplier
            ? {
              key: 'unit_price_without_commission',
              label: this.$t('unitPrice'),
              tdClass: 'text-right text-nowrap invoice-money-col',
              thClass: 'text-right invoice-money-col',
              thStyle: { width: '1%' },
            }
            : {
              key: 'unit_price',
              label: this.$t('unitPrice'),
              tdClass: 'text-right text-nowrap invoice-money-col',
              thClass: 'text-right invoice-money-col',
              thStyle: { width: '1%' },
            },
          isPOForSupplier
            ? {
              key: 'total_without_commission',
              label: this.$t('total'),
              tdClass: 'text-right text-nowrap invoice-money-col',
              thClass: 'text-right invoice-money-col',
              thStyle: { width: '1%' },
            }
            : {
              key: 'total',
              label: this.$t('total'),
              tdClass: 'text-right text-nowrap invoice-money-col',
              thClass: 'text-right invoice-money-col',
              thStyle: { width: '1%' },
            },
        ]

        if (this.showWhatsappColumn) {
          printFields.splice(3, 0, {
            key: 'whatsapp',
            label: 'Tel.',
            thClass: 'text-center',
            tdClass: 'text-center',
            thStyle: { width: '120px' },
          })
        }

        return printFields.map(field => this.applySupplierFieldRowspan(field))
      }

      // ====== MODO PANTALLA ======
      const fields = this.showWhatsappColumn
        ? [...this.fields]
        : this.fields.filter(field => field.key !== 'whatsapp')

      const deliveriesIdx = fields.findIndex(f => f.key === 'item_deliveries')
      if (this.showDeliveriesColumn) {
        if (deliveriesIdx === -1) {
          const quantityIdx = fields.findIndex(f => f.key === 'quantity')
          fields.splice(quantityIdx + 1, 0, {
            key: 'item_deliveries',
            label: this.$t('itemDeliveries.column'),
            tdClass: 'text-center invoice-compact-col',
            thClass: 'text-center invoice-compact-col',
          })
        }
      } else if (deliveriesIdx !== -1) {
        fields.splice(deliveriesIdx, 1)
      }

      // Asegurar que NO se duplique 'actions'
      const actionsIdx = fields.findIndex(f => f.key === 'actions')
      if (this.showActionsColumn) {
        if (actionsIdx === -1) {
          fields.push({
            key: 'actions',
            label: '',
            tdClass: 'text-center invoice-actions-col',
            thClass: 'text-center invoice-actions-col',
            thStyle: { width: '1%' },
          })
        }
      } else if (actionsIdx !== -1) {
        fields.splice(actionsIdx, 1)
      }

      // Asegurar que 'is_express' esté solo cuando aplica
      const deliveryIdx = fields.findIndex(f => f.key === 'is_express')
      if (this.quote.is_express) {
        if (deliveryIdx === -1) {
          fields.unshift({ key: 'is_express', label: this.$t('delivery') })
        }
      } else if (deliveryIdx !== -1) {
        fields.splice(deliveryIdx, 1)
      }

      return fields.map(field => this.applySupplierFieldRowspan(field))
    },
  },

  watch: {
    showDeliveriesColumn: {
      immediate: true,
      handler(enabled) {
        if (enabled) this.loadItemDeliveries()
      },
    },
    // Intentamos inicializar UNA SOLA VEZ cuando haya datos
    products: {
      immediate: true,
      handler() {
        this.initSupplierLabels()
      },
    },
    supplierData: {
      immediate: true,
      handler() {
        this.initSupplierLabels()
      },
    },
  },

  created() {
    // If view is purchase order and currentUser is auctioneer we replace: (total => total without comission)
    if (
      this.$route.name === 'purchaseOrder'
      && ['auctioneer', 'supplier_admin'].includes(this.currentUser.role_name)
    ) {
      const totalWithoutCommission = {
        key: 'total_without_commission',
        label: this.$t('total'),
        tdClass: 'text-center',
        thClass: 'text-center',
      }
      const unitPriceWithoutCommission = {
        key: 'unit_price_without_commission',
        label: this.$t('unitPrice'),
        tdClass: 'text-center',
        thClass: 'text-center',
      }
      const indexToDelete1 = this.fields.findIndex(f => f.key === 'total')
      const indexToDelete2 = this.fields.findIndex(f => f.key === 'unit_price')
      this.fields[indexToDelete1] = totalWithoutCommission
      this.fields[indexToDelete2] = unitPriceWithoutCommission
    }
  },

  methods: {
    ...mapActions('quotes', ['deleteQuoteItems', 'fetchQuote', 'updateOrderNumber', 'updateQuoteItemPrices', 'updateFreight', 'clearFreightOverride']),
    ...mapActions('products', ['searchSupplierBranchProducts']),
    ...mapActions('quotes', ['fetchQuoteItemDeliveries', 'registerQuoteItemDelivery', 'deleteQuoteItemDelivery']),

    deliveryRow(item) {
      const row = this.itemDeliveriesQuoteId === this.currentQuoteId ? this.itemDeliveriesByQuoteItem[item.id] : null
      return row || {
        quantity: Number(item.quantity || 0), delivered_quantity: 0, pending_quantity: Number(item.quantity || 0), fully_delivered: false, deliveries: [],
      }
    },

    formatDeliveredQuantity(value) {
      const number = Number(value || 0)
      return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/\.?0+$/, '')
    },

    deliveryPercent(item) {
      const row = this.deliveryRow(item)
      const quantity = Number(item.quantity || 0)
      if (!quantity) return 0
      return Math.min(100, Math.round((Number(row.delivered_quantity || 0) / quantity) * 100))
    },

    deliveryVariant(item) {
      const row = this.deliveryRow(item)
      if (row.fully_delivered) return 'success'
      return Number(row.delivered_quantity || 0) > 0 ? 'warning' : 'secondary'
    },

    deliveryProgressClass(item) {
      return `delivery-progress--${this.deliveryVariant(item)}`
    },

    async loadItemDeliveries() {
      if (!this.currentQuoteId) return
      try {
        await this.fetchQuoteItemDeliveries(this.currentQuoteId)
      } catch (error) {
        this.notifyDelivery(error?.response?.data?.messages?.[0] || this.$t('Error'), 'danger')
      }
    },

    openDeliveriesModal(item) {
      this.deliveriesItem = item
      this.deliveryForm = { quantity: null, delivered_at: todayLocalDate(), note: '' }
      this.$refs.itemDeliveriesModal.show()
    },

    notifyDelivery(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'danger' ? 'AlertCircleIcon' : 'TruckIcon', variant },
      })
    },

    async submitDelivery() {
      if (!this.deliveriesItem || !(this.deliveryForm.quantity > 0)) return

      this.isSavingDelivery = true
      try {
        await this.registerQuoteItemDelivery({
          quoteId: this.currentQuoteId,
          quoteItemId: this.deliveriesItem.id,
          quantity: this.deliveryForm.quantity,
          deliveredAt: this.deliveryForm.delivered_at,
          note: this.deliveryForm.note,
        })
        this.deliveryForm = { quantity: null, delivered_at: todayLocalDate(), note: '' }
        this.notifyDelivery(this.$t('itemDeliveries.registered'), 'success')
        this.fetchQuote(this.currentQuoteId)
      } catch (error) {
        this.notifyDelivery(error?.response?.data?.messages?.[0] || error?.data?.messages?.[0] || this.$t('Error'), 'danger')
      } finally {
        this.isSavingDelivery = false
      }
    },

    async removeDelivery(delivery) {
      if (!this.deliveriesItem) return
      const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('itemDeliveries.deleteConfirmation'), {
        okVariant: 'danger',
        okTitle: this.$t('delete'),
        cancelTitle: this.$t('cancel'),
        centered: true,
      })
      if (!confirmed) return

      this.deletingDeliveryId = delivery.id
      try {
        await this.deleteQuoteItemDelivery({
          quoteId: this.currentQuoteId,
          quoteItemId: this.deliveriesItem.id,
          deliveryId: delivery.id,
        })
        this.notifyDelivery(this.$t('itemDeliveries.deleted'), 'success')
        this.fetchQuote(this.currentQuoteId)
      } catch (error) {
        this.notifyDelivery(error?.response?.data?.messages?.[0] || error?.data?.messages?.[0] || this.$t('Error'), 'danger')
        await this.loadItemDeliveries()
      } finally {
        this.deletingDeliveryId = null
      }
    },
    ...mapActions('branches', ['fetchAllBranches']),

    // ===============================
    //  SNAPSHOT DE NOMBRES DE PROVEEDOR
    // ===============================
    initSupplierLabels() {
      if (!Array.isArray(this.products) || !this.products.length) return

      const labels = { ...this.supplierLabelsByItemId }
      let anyFilled = false

      this.products.forEach(item => {
        if (!item?.id) return

        let label = item.selected_supplier_branch_name
      || item.selected_supplier_name
      || item.supplier_name
      || item.branch_name
      || item.business_name
      || item.supplier
      || ''

        if (!label && item.selected_supplier_branch_id && Array.isArray(this.supplierData)) {
          const found = this.supplierData.find(s => s.id === item.selected_supplier_branch_id)
          label = found?.name || found?.branch_name || found?.supplier_name || found?.business_name || ''
        }

        if (label) anyFilled = true
        labels[item.id] = label
      })

      this.supplierLabelsByItemId = labels

      // Solo “congelar” cuando ya llenaste al menos algo real
      if (anyFilled) this.suppliersInitialized = true
    },

    getSupplierLabel(item) {
      if (!item) return ''

      // 1) Lo que viene del backend (tu caso real)
      const direct = item.selected_supplier_branch_name
    || item.selected_supplier_name
    || item.supplier_name
    || item.branch_name
    || item.business_name
    || item.supplier
    || ''

      if (direct) return direct

      // 2) Fallback por snapshot si existe
      return this.supplierLabelsByItemId[item.id] || ''
    },

    whatsappStatusText(type) {
      if (!this.isGuestView) {
        return type === 'no_phone' ? 'Sin teléfono' : 'Orden no registrada'
      }

      return type === 'no_phone' ? 'Sin tel.' : 'Sin OC'
    },

    normalizeText(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
    },

    normalizeSupplierKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
    },

    supplierGroupKey(item) {
      if (item?.selected_supplier_branch_id) return `supplier_${item.selected_supplier_branch_id}`

      const supplierLabel = this.getSupplierLabel(item)
      const normalizedSupplier = this.normalizeSupplierKey(supplierLabel)
      if (normalizedSupplier) return `supplier_name_${normalizedSupplier}`

      return `item_${item?.id || ''}`
    },

    compareRowsForDisplay(a, b) {
      const supplierKeyA = this.supplierGroupKey(a)
      const supplierKeyB = this.supplierGroupKey(b)
      if (supplierKeyA !== supplierKeyB) {
        return supplierKeyA.localeCompare(supplierKeyB)
      }

      const productNameA = this.normalizeText(a?.product_catalog?.name)
      const productNameB = this.normalizeText(b?.product_catalog?.name)
      if (productNameA !== productNameB) {
        return productNameA.localeCompare(productNameB, 'es', { sensitivity: 'base' })
      }

      const idA = String(a?.id || '')
      const idB = String(b?.id || '')
      return idA.localeCompare(idB)
    },

    attachSupplierRowspans(items) {
      const rows = Array.isArray(items) ? items.map(item => ({ ...item })) : []

      if (!this.mergeSupplierCells || !rows.length) {
        return rows.map(row => ({
          ...row,
          supplierGroupHead: true,
          supplierGroupRowspan: 1,
          supplierGroupSequence: 0,
          supplierGroupStart: true,
          supplierGroupEnd: true,
        }))
      }

      let i = 0
      let groupIndex = 0
      while (i < rows.length) {
        const key = this.supplierGroupKey(rows[i])
        let span = 1
        let j = i + 1

        while (j < rows.length && this.supplierGroupKey(rows[j]) === key) {
          span += 1
          j += 1
        }

        rows[i].supplierGroupHead = true
        rows[i].supplierGroupRowspan = span
        rows[i].supplierGroupSize = span
        rows[i].supplierGroupSequence = groupIndex
        rows[i].supplierGroupStart = true

        for (let k = i + 1; k < j; k += 1) {
          rows[k].supplierGroupHead = false
          rows[k].supplierGroupRowspan = 0
          rows[k].supplierGroupSize = span
          rows[k].supplierGroupSequence = groupIndex
          rows[k].supplierGroupStart = false
          rows[k].supplierGroupEnd = false
        }

        rows[j - 1].supplierGroupEnd = true
        i = j
        groupIndex += 1
      }

      return rows
    },

    rowClassForSupplierGroup(item, type) {
      if (type !== 'row' || !item || this.printMode) return ''
      const classes = []
      if (Number(item.supplierGroupSize || 1) > 1) classes.push('supplier-group-multi')
      if (item.supplierGroupStart) classes.push('supplier-group-start')
      if (item.supplierGroupEnd) classes.push('supplier-group-end')
      if (Number(item.supplierGroupSize || 1) > 1 && !item.supplierGroupStart && !item.supplierGroupEnd) {
        classes.push('supplier-group-middle')
      }
      if (Number(item.supplierGroupSequence || 0) % 2 === 1) classes.push('supplier-group-alt')
      if (this.canShowComparison(item)) classes.push('supplier-comparison-available')
      return classes.join(' ')
    },

    shouldRenderSupplierGroupCell(item) {
      if (!this.mergeSupplierCells) return true
      return Boolean(item?.supplierGroupHead)
    },

    getSupplierCellAttrs(item) {
      if (!this.mergeSupplierCells) return {}
      if (!item?.supplierGroupHead) return { class: 'supplier-group-cell-hidden', style: 'display: none !important;' }

      const rowspan = Number(item.supplierGroupRowspan || 1)
      return rowspan > 1
        ? { rowspan, class: 'supplier-group-cell' }
        : { class: 'supplier-group-cell' }
    },

    applySupplierFieldRowspan(field) {
      if (!field || !['supplier', 'delivery_time_hours', 'whatsapp'].includes(field.key)) return field

      const originalTdAttr = field.tdAttr
      return {
        ...field,
        tdAttr: (value, key, item) => {
          const base = typeof originalTdAttr === 'function'
            ? (originalTdAttr(value, key, item) || {})
            : (originalTdAttr || {})
          const extra = this.getSupplierCellAttrs(item)
          return { ...base, ...extra }
        },
      }
    },

    roundMoney(value) {
      return Math.round(Number(value || 0) * 100) / 100
    },

    getCustomerUnitPrice(item) {
      const explicitCustomerPrice = Number(item?.customer_unit_price)
      if (Number.isFinite(explicitCustomerPrice)) return explicitCustomerPrice

      return Number(item?.unit_price || 0) + Number(item?.unit_commission_price || 0)
    },

    displayItemUnitPrice(item) {
      return this.showSupplierNetPrices
        ? Number(item?.unit_price || 0)
        : this.getCustomerUnitPrice(item)
    },

    displayItemTotal(item) {
      return this.showSupplierNetPrices
        ? Number(item?.total_without_commission || 0)
        : Number(item?.total || 0)
    },

    canShowComparison(item) {
      if (this.printMode || this.hideComparison) return false

      return this.getComparisonOptions(item).length > 0
    },

    isComparisonOpen(item) {
      return Boolean(item?.id && this.expandedComparisonItemIds[item.id])
    },

    toggleComparison(item) {
      if (!item?.id || !this.canShowComparison(item)) return

      this.expandedComparisonItemIds = {
        ...this.expandedComparisonItemIds,
        [item.id]: !this.expandedComparisonItemIds[item.id],
      }
    },

    getComparisonOptions(item) {
      const unitPrices = Array.isArray(item?.unit_prices) ? item.unit_prices : []
      const selectedSupplierId = item?.selected_supplier_branch_id
        ? String(item.selected_supplier_branch_id)
        : ''
      const selectedSupplierName = this.getSupplierLabel(item)
      const selectedUnitPrice = this.roundMoney(this.getCustomerUnitPrice(item))

      const validOptions = unitPrices
        .map((option, index) => {
          const unitPrice = this.resolveComparisonUnitPrice(option)

          return {
            key: `${option?.supplier_branch_id || 'supplier'}-${index}`,
            supplierId: option?.supplier_branch_id ? String(option.supplier_branch_id) : '',
            supplierName: this.resolveComparisonSupplierName(option, index),
            unitPrice,
          }
        })
        .filter(option => Number.isFinite(option.unitPrice) && option.unitPrice > 0)
        .sort((a, b) => a.unitPrice - b.unitPrice)

      const hasSelectedOption = validOptions.some(option => {
        const sameSupplierId = selectedSupplierId && option.supplierId === selectedSupplierId
        const sameSupplierName = selectedSupplierName
          && this.normalizeSupplierKey(option.supplierName) === this.normalizeSupplierKey(selectedSupplierName)
        const samePrice = this.roundMoney(option.unitPrice) === selectedUnitPrice
        return sameSupplierId || sameSupplierName || samePrice
      })

      if (!hasSelectedOption && selectedUnitPrice > 0) {
        validOptions.push({
          key: `selected-${item?.id || selectedSupplierId || selectedSupplierName || 'item'}`,
          supplierId: selectedSupplierId,
          supplierName: selectedSupplierName || 'Proveedor seleccionado',
          unitPrice: selectedUnitPrice,
        })
        validOptions.sort((a, b) => a.unitPrice - b.unitPrice)
      }

      if (!validOptions.length) return []

      const normalizedSelectedSupplierName = this.normalizeSupplierKey(selectedSupplierName)
      const lowestPrice = validOptions[0].unitPrice

      return validOptions.map(option => {
        const isSelectedById = selectedSupplierId && option.supplierId === selectedSupplierId
        const isSelectedByName = normalizedSelectedSupplierName
          && this.normalizeSupplierKey(option.supplierName) === normalizedSelectedSupplierName
        const isSelectedByPrice = !selectedSupplierId
          && !normalizedSelectedSupplierName
          && this.roundMoney(option.unitPrice) === selectedUnitPrice

        return {
          ...option,
          isSelected: Boolean(isSelectedById || isSelectedByName || isSelectedByPrice),
          isLowest: this.roundMoney(option.unitPrice) === this.roundMoney(lowestPrice),
        }
      })
    },

    comparisonToggleLabel(item) {
      return this.getComparisonOptions(item).length > 1 ? 'Ver detalle comparativo' : 'Ver detalle'
    },

    comparisonSubtitle(item) {
      if (this.getComparisonOptions(item).length > 1) {
        return 'Comparación recibida para este material. El proveedor seleccionado es el que se usa en la cotización.'
      }

      return 'Detalle del proveedor seleccionado para este material.'
    },

    resolveComparisonUnitPrice(option) {
      const candidates = [
        option?.customer_unit_price,
        option?.unit_price,
        option?.supplier_unit_price,
        option?.price,
      ]

      const found = candidates
        .map(value => Number(value))
        .find(value => Number.isFinite(value) && value > 0)

      return found || 0
    },

    resolveComparisonSupplierName(option, index) {
      return option?.supplier_branch_name
        || option?.selected_supplier_branch_name
        || option?.supplier_name
        || option?.branch_name
        || option?.business_name
        || `Proveedor ${index + 1}`
    },

    getComparisonSavings(item) {
      const options = this.getComparisonOptions(item)
      if (options.length < 2) {
        // Sin las ofertas por proveedor (vista del cliente) se usa el ahorro que calcula el backend.
        const serverSavings = Number(item?.estimated_savings) || 0
        return {
          hasComparison: serverSavings > 0,
          amount: serverSavings,
          averageUnitPrice: 0,
          selectedUnitPrice: 0,
          quantity: Number(item?.quantity || 0),
        }
      }

      const selectedOption = options.find(option => option.isSelected) || null
      const selectedUnitPrice = selectedOption
        ? Number(selectedOption.unitPrice || 0)
        : Number(this.getCustomerUnitPrice(item) || 0)
      const participantPrices = options
        .map(option => Number(option.unitPrice))
        .filter(value => Number.isFinite(value) && value > 0)

      if (!participantPrices.length || !Number.isFinite(selectedUnitPrice) || selectedUnitPrice <= 0) {
        return {
          hasComparison: false,
          amount: 0,
          averageUnitPrice: 0,
          selectedUnitPrice: 0,
          quantity: Number(item?.quantity || 0),
        }
      }

      const averageUnitPrice = participantPrices.reduce((sum, price) => sum + price, 0) / participantPrices.length
      const quantity = Number(item?.quantity || 0)

      return {
        hasComparison: true,
        amount: this.roundMoney((averageUnitPrice - selectedUnitPrice) * quantity),
        averageUnitPrice: this.roundMoney(averageUnitPrice),
        selectedUnitPrice: this.roundMoney(selectedUnitPrice),
        quantity,
      }
    },

    savingsLabel(savings) {
      const amount = Number(savings?.amount || 0)
      const absoluteAmount = Math.abs(amount)
      if (amount > 0) return `Ahorro vs promedio: $${this.$options.filters.money(absoluteAmount)} MXN`
      if (amount < 0) return `Sobre promedio: $${this.$options.filters.money(absoluteAmount)} MXN`
      return 'Sin diferencia vs promedio'
    },

    formatQuantity(value) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) return '0'
      return this.$options.filters.money(numericValue)
    },

    formatMoney(value) {
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue) || numericValue <= 0) return 'Sin precio'

      return `$${this.$options.filters.money(numericValue)} MXN`
    },

    getUnitResult(basePrice, customerUnitPrice) {
      return Number(customerUnitPrice || 0) - Number(basePrice || 0)
    },

    normalizeOptionLabel(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
    },

    async ensureAllSuppliersLoaded() {
      if (this.allSuppliersLoaded && Object.keys(this.allSuppliersById).length) {
        return
      }

      if (this.allSuppliersLoadPromise) {
        await this.allSuppliersLoadPromise
        return
      }

      this.allSuppliersLoadPromise = (async () => {
        const branchesResponse = await this.fetchAllBranches({
          params: {
            compact: true,
            meta: { pagination: { all: true } },
          },
        })
        const branches = Array.isArray(branchesResponse?.data) ? branchesResponse.data : []
        const allSuppliersById = {}

        branches.forEach(branch => {
          if (!branch?.id) return

          allSuppliersById[String(branch.id)] = {
            id: String(branch.id),
            name: branch.name || this.$t('supplier'),
          }
        })

        this.allSuppliersById = allSuppliersById
        this.allSuppliersLoaded = true
      })()

      try {
        await this.allSuppliersLoadPromise
      } finally {
        this.allSuppliersLoadPromise = null
      }
    },

    async hydrateEditSupplierOptions(item) {
      this.isLoadingEditSuppliers = true

      try {
        await this.ensureAllSuppliersLoaded()
        if (!this.editingItem || this.editingItem.id !== item.id) return
        this.editSupplierOptions = this.buildEditSupplierOptions(item)
      } catch (error) {
        console.error('Error obteniendo proveedores editables de la partida:', error)
      } finally {
        if (this.editingItem && this.editingItem.id === item.id) {
          this.isLoadingEditSuppliers = false
        }
      }
    },

    getSupplierUnitPriceOptions(item) {
      return Array.isArray(item?.unit_prices) ? item.unit_prices : []
    },

    findSupplierUnitPriceOption(item, supplierBranchId) {
      const supplierId = String(supplierBranchId || '')
      if (!supplierId) return null

      return this.getSupplierUnitPriceOptions(item).find(
        option => String(option?.supplier_branch_id || '') === supplierId,
      ) || null
    },

    buildEditSupplierOptions(item) {
      const optionMap = new Map()

      Object.values(this.allSuppliersById).forEach(supplier => {
        if (!supplier?.id) return

        optionMap.set(String(supplier.id), {
          value: String(supplier.id),
          text: supplier.name || this.$t('supplier'),
          supplier_branch_name: supplier.name || this.$t('supplier'),
        })
      })

      const currentSupplierId = String(item?.selected_supplier_branch_id || '')
      const currentSupplierName = this.getSupplierLabel(item)
      if (currentSupplierId && !optionMap.has(currentSupplierId)) {
        if (currentSupplierName) {
          optionMap.set(currentSupplierId, {
            value: currentSupplierId,
            text: currentSupplierName,
            supplier_branch_name: currentSupplierName,
          })
        }
      }

      return Array.from(optionMap.values()).sort((a, b) => this.normalizeOptionLabel(a.text)
        .localeCompare(this.normalizeOptionLabel(b.text), 'es', { sensitivity: 'base' }))
    },

    currentProductOption(item) {
      const product = item?.product_catalog
      if (!product) return null

      return {
        value: String(item.product_catalog_id),
        label: product.name,
        meta: [product.brand, product.sku].filter(Boolean).join(' · '),
        price_list: null,
      }
    },

    buildProductOption(stockable) {
      const product = stockable?.product_catalog || {}

      return {
        value: String(stockable.product_catalog_id),
        label: product.name || this.$t('product'),
        meta: [
          product.brand,
          product.sku,
          `$${this.$options.filters.money(stockable.price_list || 0)}`,
          stockable.out_of_stock ? this.$t('outOfStock') : null,
        ].filter(Boolean).join(' · '),
        price_list: Number(stockable.price_list || 0),
      }
    },

    async loadEditProducts(term = '') {
      const supplierBranchId = this.editForm.supplier_branch_id
      const item = this.editingItem
      if (!supplierBranchId || !item) {
        this.editProductOptions = []
        return
      }

      this.editProductSearchSeq += 1
      const seq = this.editProductSearchSeq
      this.isLoadingEditProducts = true
      try {
        const results = await this.searchSupplierBranchProducts({ supplierBranchId, term })
        if (seq !== this.editProductSearchSeq) return
        if (this.editingItem?.id !== item.id || this.editForm.supplier_branch_id !== supplierBranchId) return

        const options = results.map(stockable => this.buildProductOption(stockable))
        const selectedValue = String(this.editForm.product_catalog_id || '')
        const pinned = [
          this.editProductOptions.find(option => option.value === selectedValue),
          term ? null : this.currentProductOption(item),
        ].filter(Boolean)
        pinned.forEach(option => {
          if (!options.some(candidate => candidate.value === option.value)) options.unshift(option)
        })
        this.editProductOptions = options
      } catch (error) {
        if (seq === this.editProductSearchSeq) this.editProductOptions = [this.currentProductOption(item)].filter(Boolean)
      } finally {
        if (seq === this.editProductSearchSeq) this.isLoadingEditProducts = false
      }
    },

    searchEditProducts(term) {
      clearTimeout(this.editProductSearchTimer)
      this.editProductSearchTimer = setTimeout(() => this.loadEditProducts(term), 300)
    },

    handleEditProductChange(productCatalogId) {
      if (!this.editingItem || !productCatalogId) return
      if (String(productCatalogId) === String(this.editingItem.product_catalog_id)) return

      const option = this.editProductOptions.find(candidate => candidate.value === String(productCatalogId))
      if (!option || option.price_list === null) return

      const margin = Number(this.editForm.customer_unit_price || 0) - Number(this.editForm.unit_price || 0)
      this.editForm.unit_price = option.price_list
      this.editForm.customer_unit_price = Math.max(option.price_list + margin, 0)
    },

    handleEditSupplierChange(selectedSupplierBranchId) {
      if (!this.editingItem) return

      this.editForm.product_catalog_id = String(this.editingItem.product_catalog_id || '')
      this.loadEditProducts()

      const selectedOption = this.findSupplierUnitPriceOption(this.editingItem, selectedSupplierBranchId)
      if (!selectedOption) return

      if (selectedOption.supplier_unit_price !== undefined) {
        this.editForm.unit_price = Number(selectedOption.supplier_unit_price || 0)
      }
      if (selectedOption.customer_unit_price !== undefined) {
        this.editForm.customer_unit_price = Number(selectedOption.customer_unit_price || 0)
      }
    },

    unitResultClass(basePrice, customerUnitPrice) {
      return this.getUnitResult(basePrice, customerUnitPrice) < 0 ? 'text-danger' : 'text-success'
    },

    unitResultLabel(basePrice, customerUnitPrice) {
      const result = this.getUnitResult(basePrice, customerUnitPrice)
      const labelKey = result < 0 ? 'invoiceEditItemLossLabel' : 'invoiceEditItemMarginLabel'
      return `${this.$t(labelKey)}: $${this.$options.filters.money(Math.abs(result))}`
    },

    deleteItem(product) {
      this.pendingDeleteItem = product
      this.$refs.deleteQuoteItemModal.show()
    },

    async confirmDeleteItem() {
      if (this.isQuoteItemsLocked) {
        this.pendingDeleteItem = null
        alert(this.$t('quoteItemsLockedForAccounting'))
        return
      }

      if (!this.pendingDeleteItem) return

      const items = [{ quote_item_id: this.pendingDeleteItem.id }]
      try {
        await this.deleteQuoteItems({
          quoteId: this.currentQuoteId,
          items,
        })
        this.pendingDeleteItem = null
        this.$emit('delete-user')
      } catch (error) {
        this.pendingDeleteItem = null
        if (this.isLockedError(error)) {
          alert(this.$t('quoteItemsLockedForAccounting'))
          return
        }
        alert(this.$t('invoiceEditItemSaveError'))
      }
    },

    isOrderNumberFormOpen(item) {
      return this.editingOrderNumberId === item.id || !item.order_number
    },

    startEditOrderNumber(item) {
      this.$set(item, 'tempOrderNumber', item.order_number || '')
      this.editingOrderNumberId = item.id
    },

    cancelEditOrderNumber() {
      this.editingOrderNumberId = null
    },

    async saveOrderNumber(item) {
      if (!item.tempOrderNumber || item.tempOrderNumber.trim() === '') {
        alert('Por favor, ingresa un número de pedido válido.')
        return
      }

      const orderNumber = item.tempOrderNumber.trim()

      try {
        this.isSavingOrderNumber = true
        await this.updateOrderNumber({
          quoteId: this.currentQuoteId,
          quoteItemId: item.id, // ID del QuoteItem
          orderNumber,
        })

        // `filteredProducts` trabaja sobre copias de `products`, así que el valor
        // hay que reflejarlo también en el origen para que no se pierda al
        // recalcularse la lista.
        const source = this.products.find(product => product.id === item.id)
        if (source) this.$set(source, 'order_number', orderNumber)

        this.$set(item, 'order_number', orderNumber)
        this.$set(item, 'tempOrderNumber', '')
        this.editingOrderNumberId = null
        alert('Número de pedido guardado correctamente.')
      } catch (error) {
        console.error('Error guardando el número de pedido:', error)
        const backendMessage = error?.data?.messages?.[0] || error?.response?.data?.messages?.[0]
        alert(backendMessage || 'Hubo un problema al guardar el número de pedido.')
      } finally {
        this.isSavingOrderNumber = false
      }
    },

    getPhone(item) {
      const orderNumber = item.order_number
      const message = `Solicito estatus de ORDEN "${orderNumber}" DE PLATAFORMA DIGITAL YOC`

      // ✅ ya no dependemos de supplierData
      const phone = item?.selected_supplier_branch_phone || null

      return { phone, message }
    },

    async openPriceModal(item) {
      if (!this.canOpenPriceModal) {
        alert(this.$t('quoteItemsLockedForAccounting'))
        return
      }

      this.editingItem = item
      this.editForm.supplier_branch_id = item.selected_supplier_branch_id
        ? String(item.selected_supplier_branch_id)
        : null
      this.editForm.unit_price = Number(item.unit_price || 0)
      this.editForm.customer_unit_price = this.getCustomerUnitPrice(item)
      this.editForm.quantity = Number(item.quantity || 1)
      this.editForm.product_catalog_id = String(item.product_catalog_id || '')
      this.editProductOptions = [this.currentProductOption(item)].filter(Boolean)
      this.editSupplierOptions = this.allSuppliersLoaded ? this.buildEditSupplierOptions(item) : []
      this.$refs.editPricesModal.show()
      if (!this.allSuppliersLoaded) {
        this.hydrateEditSupplierOptions(item)
      }
      if (!this.isOnlySupplierPriceEditable) {
        this.loadEditProducts()
      }
    },

    async savePrices(bvModalEvt) {
      if (bvModalEvt && bvModalEvt.preventDefault) bvModalEvt.preventDefault()

      if (!this.editingItem) return
      if (!this.canOpenPriceModal) {
        alert(this.$t('quoteItemsLockedForAccounting'))
        return
      }

      const { isOnlySupplierPriceEditable, editMergeTarget } = this
      const productChanged = !isOnlySupplierPriceEditable
        && this.editForm.product_catalog_id
        && String(this.editForm.product_catalog_id) !== String(this.editingItem.product_catalog_id)

      if (editMergeTarget) {
        const mergeQty = Number(this.editForm.quantity || 0)
        if (mergeQty <= 0 || Number.isNaN(mergeQty)) {
          alert(this.$t('invoiceEditItemInvalidValues'))
          return
        }

        try {
          this.isSavingPrices = true
          await this.updateQuoteItemPrices({
            quoteId: this.currentQuoteId,
            quoteItemId: this.editingItem.id,
            productCatalogId: this.editForm.product_catalog_id,
            unitPrice: Number(this.editingItem.unit_price || 0),
            quantity: mergeQty,
          })
          await this.fetchQuote(this.currentQuoteId)
          this.$emit('items-updated')
          this.$refs.editPricesModal.hide()
        } catch (error) {
          alert(error?.response?.data?.messages?.[0] || error?.data?.messages?.[0] || this.$t('invoiceEditItemSaveError'))
        } finally {
          this.isSavingPrices = false
        }
        return
      }

      const base = Number(this.editForm.unit_price || 0)
      const currentCustomerUnitPrice = this.getCustomerUnitPrice(this.editingItem)
      const currentQuantity = Number(this.editingItem.quantity || 0)
      const currentSupplierBranchId = this.editingItem.selected_supplier_branch_id
      const customer = isOnlySupplierPriceEditable
        ? currentCustomerUnitPrice
        : Number(this.editForm.customer_unit_price || 0)
      const qty = isOnlySupplierPriceEditable
        ? currentQuantity
        : Number(this.editForm.quantity || 0)
      const supplierBranchId = this.editForm.supplier_branch_id || currentSupplierBranchId

      if (
        !supplierBranchId
        || base < 0
        || Number.isNaN(base)
        || (!isOnlySupplierPriceEditable && (
          customer < 0
          || qty <= 0
          || Number.isNaN(customer)
          || Number.isNaN(qty)
        ))
      ) {
        alert(this.$t('invoiceEditItemInvalidValues'))
        return
      }

      try {
        this.isSavingPrices = true
        await this.updateQuoteItemPrices({
          quoteId: this.currentQuoteId,
          quoteItemId: this.editingItem.id,
          unitPrice: base,
          supplierBranchId,
          ...(productChanged ? { productCatalogId: this.editForm.product_catalog_id } : {}),
          ...(!isOnlySupplierPriceEditable ? {
            customerUnitPrice: customer,
            quantity: qty,
          } : {}),
        })

        const selectedSupplierOption = this.findSupplierUnitPriceOption(this.editingItem, supplierBranchId)
        const selectedSupplierData = this.allSuppliersById[String(supplierBranchId)] || null
        const selectedEditSupplierOption = this.editSupplierOptions.find(
          option => String(option?.value || '') === String(supplierBranchId),
        ) || null
        this.editingItem.unit_price = base
        this.editingItem.customer_unit_price = customer
        this.editingItem.unit_commission_price = customer - base
        this.editingItem.quantity = qty
        this.editingItem.total_without_commission = base * qty
        this.editingItem.total = customer * qty
        if (supplierBranchId) {
          this.editingItem.selected_supplier_branch_id = supplierBranchId
          this.editingItem.selected_supplier_branch_name = selectedSupplierData?.name
            || selectedEditSupplierOption?.supplier_branch_name
            || selectedSupplierOption?.supplier_branch_name
            || this.editingItem.selected_supplier_branch_name
          if (this.editingItem.selected_supplier_branch_name) {
            this.supplierLabelsByItemId = {
              ...this.supplierLabelsByItemId,
              [this.editingItem.id]: this.editingItem.selected_supplier_branch_name,
            }
          }
        }

        await this.fetchQuote(this.currentQuoteId)
        this.$emit('items-updated')

        this.$refs.editPricesModal.hide()
      } catch (e) {
        if (this.isLockedError(e)) {
          alert(this.$t('quoteItemsLockedForAccounting'))
          return
        }
        console.error(e)
        const backendMessage = e?.response?.data?.messages?.[0] || e?.data?.messages?.[0]
        alert(backendMessage || this.$t('invoiceEditItemSaveError'))
      } finally {
        this.isSavingPrices = false
      }
    },
    supplierFreight(item) {
      const supplierBranchId = String(item?.selected_supplier_branch_id || '')
      const overrides = this.activeQuote?.freight_overrides || {}
      const overridden = Object.prototype.hasOwnProperty.call(overrides, supplierBranchId)
      const amount = overridden
        ? Number(overrides[supplierBranchId] || 0)
        : this.filteredProducts
          .filter(product => String(product?.selected_supplier_branch_id || '') === supplierBranchId)
          .reduce((sum, product) => sum + Number(product?.shipping_price || 0), 0)

      return {
        supplierBranchId,
        supplierName: this.getSupplierLabel(item),
        amount: Number(amount.toFixed(2)),
        overridden,
      }
    },

    openFreightModal(item) {
      if (!this.canEditFreight) return

      this.editingFreight = this.supplierFreight(item)
      this.freightForm.amount = this.editingFreight.amount
      this.$refs.editFreightModal.show()
    },

    async saveFreight() {
      if (!this.editingFreight || this.isSavingFreight) return

      const amount = Number(this.freightForm.amount || 0)
      if (Number.isNaN(amount) || amount < 0) {
        alert('El flete debe ser un número mayor o igual a cero.')
        return
      }

      await this.runFreightUpdate(
        () => this.updateFreight({
          quoteId: this.currentQuoteId,
          freightOverrides: { [this.editingFreight.supplierBranchId]: amount },
        }),
        'No fue posible actualizar el flete.',
      )
    },

    async restoreAutomaticFreight() {
      if (!this.editingFreight || this.isSavingFreight) return

      await this.runFreightUpdate(
        () => this.clearFreightOverride({
          quoteId: this.currentQuoteId,
          supplierBranchId: this.editingFreight.supplierBranchId,
        }),
        'No fue posible restablecer el flete automático.',
      )
    },

    async runFreightUpdate(action, errorMessage) {
      try {
        this.isSavingFreight = true
        await action()
        await this.fetchQuote(this.currentQuoteId)
        this.$emit('items-updated')
        this.$refs.editFreightModal.hide()
      } catch (e) {
        console.error(e)
        const backendMessage = e?.response?.data?.messages?.[0] || e?.data?.messages?.[0]
        alert(backendMessage || errorMessage)
      } finally {
        this.isSavingFreight = false
      }
    },

    isLockedError(error) {
      return (error?.data?.code === 'QUOTE_ITEMS_LOCKED')
        || (error?.response?.data?.code === 'QUOTE_ITEMS_LOCKED')
    },

    whatsUrl(item) {
      const { phone, message } = this.getPhone(item)
      return `https://wa.me/52${phone}?text=${encodeURIComponent(message)}`
    },

    formatHours(val) {
      if (val === null || val === undefined || val === '') return '--'
      const n = Number(val)
      if (Number.isNaN(n)) return '--'
      return n.toFixed(2)
    },
  },
}
</script>

<style>
.whatsapp-icon-link svg {
  width: 24px;
  height: 24px;
  transition: transform 0.2s ease;
}

.whatsapp-icon-link:hover svg {
  transform: scale(1.2);
  filter: brightness(1.2);
}

.print-whatsapp {
  display: flex;
  justify-content: center;
  align-items: center;
}

.print-whatsapp-link {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #101828;
  text-decoration: none;
}

.print-whatsapp-link svg {
  width: 28px;
  height: 28px;
}

.print-whatsapp-hint {
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
  color: #25D366;
}

.invoice-products-table-wrap td.supplier-group-cell {
  vertical-align: middle !important;
}

.supplier-group-label {
  align-items: center;
  display: flex;
  justify-content: center;
}

/* ===== Vista de tarjetas agrupadas por proveedor (móvil) ===== */
.invoice-supplier-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invoice-supplier-card {
  border: 1px solid #e4e7ec;
  border-radius: 0.5rem;
  overflow: hidden;
}

.invoice-supplier-card__header {
  align-items: center;
  background: #f8f9fc;
  border-bottom: 1px solid #e4e7ec;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
}

.invoice-supplier-card__name {
  font-size: 0.85rem;
}

.invoice-supplier-card__delivery {
  color: #667085;
  font-size: 0.75rem;
}

.invoice-product-card {
  border-bottom: 1px solid #f0f1f4;
  padding: 0.75rem 0.85rem;
}

.invoice-product-card:last-of-type {
  border-bottom: 0;
}

.invoice-product-card__top {
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.invoice-product-card__title {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.invoice-product-card__product {
  font-weight: 600;
  overflow-wrap: break-word;
}

.invoice-product-card__brand {
  color: #667085;
  font-size: 0.75rem;
}

.invoice-product-card__amount {
  font-weight: 700;
  white-space: nowrap;
}

.invoice-product-card__meta {
  align-items: center;
  color: #475467;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 0.75rem;
  font-size: 0.8rem;
  margin-top: 0.35rem;
}

.invoice-product-card__savings {
  color: #067647;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.35rem;
}

.invoice-product-card__savings--negative {
  color: #b42318;
}

/* El comparativo reutiliza los estilos .supplier-comparison-* de la vista web.
   Dentro de la tarjeta solo se ajusta el margen (la tarjeta ya tiene padding)
   y se permite que el encabezado envuelva en pantallas angostas. */
.invoice-supplier-cards .supplier-comparison-row {
  margin: 0.5rem 0 0;
}

.invoice-supplier-cards .supplier-comparison-row__header {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.invoice-product-card__whatsapp {
  margin-top: 0.5rem;
}

.invoice-product-card__whatsapp-link {
  align-items: center;
  color: #25d366;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 600;
}

.invoice-product-card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.6rem;
}

.invoice-supplier-card__subtotal {
  background: #f8f9fc;
  border-top: 1px solid #e4e7ec;
  display: flex;
  justify-content: space-between;
  padding: 0.65rem 0.85rem;
}

.invoice-products-table-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.invoice-products-table-wrap .table {
  border-bottom: 1px solid #d0d5dd;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 640px;
  width: 100%;
}

.invoice-products-table-wrap .table th,
.invoice-products-table-wrap .table td {
  border-top: 0 !important;
  overflow-wrap: break-word;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.invoice-products-table-wrap .table th.invoice-product-col,
.invoice-products-table-wrap .table td.invoice-product-col {
  min-width: 150px;
}

.invoice-products-table-wrap .table th.invoice-supplier-col,
.invoice-products-table-wrap .table td.invoice-supplier-col {
  width: 22%;
  min-width: 140px;
}

.invoice-products-table-wrap .table th.invoice-compact-col,
.invoice-products-table-wrap .table td.invoice-compact-col {
  width: 1%;
  white-space: nowrap;
}

.delivery-progress {
  display: inline-flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  min-width: 64px;
  padding: 0.35rem 0.4rem !important;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.1;
}

.delivery-progress--secondary {
  color: #82868b !important;
}

.delivery-progress--warning {
  color: #ff9f43 !important;
}

.delivery-progress--success {
  color: #28c76f !important;
}

.delivery-progress__bar {
  width: 100%;
  background-color: #eceef1;
}

.invoice-products-table-wrap .supplier-group-label .badge {
  white-space: normal;
  text-align: left;
  line-height: 1.25;
}

.invoice-products-table-wrap .table thead th {
  background: #f8f9fc;
  border-bottom: 1px solid #d0d5dd !important;
  color: #667085;
  font-size: 0.78rem;
  font-weight: 700;
}

.invoice-products-table-wrap .table tbody tr {
  box-shadow: inset 0 -1px 0 #e4e7ec;
}

.invoice-products-table-wrap .table tbody tr:last-child {
  box-shadow: inset 0 -1px 0 #d0d5dd;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-multi:not(.supplier-group-end) {
  box-shadow: none;
}

.invoice-products-table-wrap .table tbody tr > td {
  background-clip: padding-box;
  padding-bottom: 0.9rem;
  padding-top: 0.9rem;
}

.invoice-products-table-wrap .table th.text-right,
.invoice-products-table-wrap .table td.text-right {
  white-space: nowrap;
}

/* width:1% colapsa la columna al ancho del texto y deja los importes pegados al borde.
   El mínimo le da aire sin robarle espacio a la descripción del producto. */
.invoice-products-table-wrap .table th.invoice-money-col,
.invoice-products-table-wrap .table td.invoice-money-col {
  width: 1%;
  min-width: 84px;
  padding-right: 0.8rem;
  white-space: nowrap;
}

.invoice-stacked-header {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.05;
}

.supplier-comparison-toggle {
  align-items: center;
  align-self: flex-start;
  background: #ffffff !important;
  border: 1px solid #7367f0 !important;
  border-radius: 6px !important;
  box-shadow: 0 2px 6px rgba(115, 103, 240, 0.16) !important;
  color: #2f1b66 !important;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 0.5rem;
  min-height: 30px;
  padding: 0.35rem 0.65rem !important;
}

.supplier-comparison-toggle:hover,
.supplier-comparison-toggle:focus {
  background: #f3f1ff !important;
  border-color: #5a2fc2 !important;
  color: #5a2fc2 !important;
  text-decoration: none;
}

.supplier-comparison-savings {
  align-self: flex-start;
  background: #ecfdf3;
  border: 1px solid #abefc6;
  border-radius: 6px;
  color: #027a48;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.15;
  margin-top: 0.4rem;
  padding: 0.3rem 0.55rem;
}

.supplier-comparison-savings--negative {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #c2410c;
}

.supplier-comparison-row {
  background: #fbfbfe;
  border: 1px solid #e2dfef;
  border-radius: 8px;
  margin: 0.25rem 0.75rem 0.75rem;
  padding: 0.9rem;
}

.supplier-comparison-row__header {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 0.8rem;
}

.supplier-comparison-row__title {
  color: #2f1b66;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
}

.supplier-comparison-row__subtitle {
  color: #6e6b7b;
  font-size: 0.82rem;
  line-height: 1.35;
  margin-top: 0.25rem;
}

.supplier-comparison-row__badge {
  background: #ecfdf3;
  border: 1px solid #abefc6;
  border-radius: 999px;
  color: #027a48;
  flex: 0 0 auto;
  font-size: 0.78rem;
  font-weight: 700;
  max-width: 260px;
  overflow: hidden;
  padding: 0.28rem 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-comparison-summary {
  background: #ffffff;
  border: 1px solid #d1fadf;
  border-radius: 8px;
  margin-bottom: 0.8rem;
  padding: 0.7rem 0.85rem;
}

.supplier-comparison-summary__amount {
  color: #027a48;
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.25;
}

.supplier-comparison-summary__amount--negative {
  color: #c2410c;
}

.supplier-comparison-summary__meta {
  color: #6e6b7b;
  font-size: 0.8rem;
  line-height: 1.35;
  margin-top: 0.25rem;
}

.supplier-comparison-options {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

.supplier-comparison-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  min-height: 54px;
  padding: 0.65rem 0.75rem;
}

.supplier-comparison-option--lowest {
  border-color: #d1fadf;
}

.supplier-comparison-option--selected {
  background: #ecfdf3;
  border-color: #12b76a;
  box-shadow: inset 4px 0 0 #12b76a;
}

.supplier-comparison-option--locked {
  background: #fbfaff;
  border-style: dashed;
  color: #6e6b7b;
}

.supplier-comparison-option--locked .locked-dots {
  font-size: 1.25rem;
  letter-spacing: 0.12em;
  line-height: 1;
  color: #b2aebf;
  margin-right: 0.4rem;
}

.locked-unlock {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
}

.supplier-comparison-option__supplier {
  color: #3d3759;
  display: flex;
  flex-direction: column;
  font-size: 0.86rem;
  font-weight: 700;
  gap: 0.25rem;
  min-width: 0;
}

.supplier-comparison-option__supplier > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}

.supplier-comparison-option__price {
  color: #2f1b66;
  flex: 0 0 auto;
  font-size: 0.86rem;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  margin-left: 0.75rem;
  text-align: right;
}

.invoice-products-table-wrap .table tbody tr.supplier-comparison-available td:first-child {
  border-left: 3px solid #d8cde8;
}

.supplier-freight {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
  white-space: nowrap;
}

.supplier-hours {
  font-size: 11px;
  margin-top: 2px;
}

.supplier-freight--card {
  margin: 0 0 8px;
  padding: 0 2px;
}

.supplier-freight__label {
  color: #b4b7bd;
}

.supplier-freight__amount {
  font-weight: 600;
}

.supplier-freight__badge {
  font-size: 9px;
  padding: 2px 4px;
}

.supplier-freight__action {
  padding: 2px 4px;
  line-height: 1;
}

.invoice-item-actions-toggle {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 999px !important;
  background: transparent !important;
  color: #6e6b7b !important;
  box-shadow: none !important;
}

.invoice-item-actions-toggle:hover,
.invoice-item-actions-toggle:focus,
.invoice-item-actions-toggle:active {
  background: #f2f4f7 !important;
  color: #3b4256 !important;
}

.invoice-item-actions-menu .dropdown-item.text-danger,
.invoice-item-actions-menu .dropdown-item.text-danger:hover {
  color: #ea5455 !important;
}

.invoice-products-table-wrap .table th.invoice-actions-col,
.invoice-products-table-wrap .table td.invoice-actions-col {
  width: 1%;
  min-width: 48px;
  white-space: nowrap;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
}

.order-number-form {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 96px;
}

.order-number-form__actions {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.order-number-edit {
  font-size: 0.7rem;
  font-weight: 600;
  margin-top: 0.25rem;
  padding: 0.15rem 0.4rem;
}

.invoice-products-table-wrap .whatsapp-status {
  display: inline-block;
  max-width: 82px;
  white-space: normal;
  line-height: 1.15;
  font-size: 0.82rem;
}

.invoice-products-table-wrap--guest .table th,
.invoice-products-table-wrap--guest .table td {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.invoice-products-table-wrap--guest .table th.invoice-actions-col,
.invoice-products-table-wrap--guest .table td.invoice-actions-col {
  min-width: 44px;
  width: 1%;
  padding-right: 0.3rem;
}

.invoice-products-table-wrap--guest .whatsapp-status {
  max-width: 58px;
  font-size: 0.78rem;
  text-align: center;
}

.invoice-products-table-wrap--customer .table th,
.invoice-products-table-wrap--customer .table td {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.invoice-products-table-wrap--customer .table th.invoice-actions-col,
.invoice-products-table-wrap--customer .table td.invoice-actions-col {
  min-width: 44px;
  width: 1%;
  padding-right: 0.3rem;
}

.invoice-products-table-wrap--customer .whatsapp-status {
  max-width: 64px;
  font-size: 0.8rem;
  text-align: center;
}

.invoice-products-table-wrap td.supplier-group-cell-hidden {
  padding: 0 !important;
  border: 0 !important;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-start td {
  border-top: 1px solid #d0d5dd !important;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-end td {
  border-bottom: 1px solid #d0d5dd !important;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-multi:not(.supplier-group-end) td {
  border-bottom: 0 !important;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-middle td {
  border-top: 0 !important;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-alt td {
  background-color: #fbfcff;
}

.invoice-products-table-wrap .table tbody tr.supplier-group-alt:hover td {
  background-color: #f5f7ff;
}

.invoice-products-table-wrap .table tbody tr:hover td {
  background-color: #fafbff;
}

.invoice-products-table-wrap .table tbody tr.b-table-details,
.invoice-products-table-wrap .table tbody tr.b-table-details td {
  background: #fbfbfe;
  box-shadow: inset 0 -1px 0 #d0d5dd;
}

@media print {
  .supplier-comparison-toggle,
  .supplier-comparison-row {
    display: none !important;
  }

  .invoice-products-table-wrap {
    overflow: visible !important;
  }

  .invoice-products-table-wrap .table {
    min-width: 0 !important;
    width: 100% !important;
  }

  .invoice-products-table-wrap .table tbody tr.supplier-group-start td,
  .invoice-products-table-wrap .table tbody tr.supplier-group-end td {
    border-top-width: 1px !important;
    border-bottom-width: 1px !important;
    border-color: #d0d5dd !important;
  }

  .invoice-products-table-wrap .table tbody tr.supplier-group-alt td {
    background-color: transparent !important;
  }

  .print-compact .table td.supplier-group-cell,
  .quote-cost-resume .table td.supplier-group-cell {
    vertical-align: top !important;
  }
}
</style>
